import React from 'react';
import PropTypes from 'prop-types';
import { useLabels, usePrefs } from 'ws-scripts/modules/common';
import { isPrefValueEnabled } from '../utils/prefUtils';
import useSearchPanel from '../hooks/useSearchPanel';

/**
 * HeaderSearchButton — desktop header magnifier. Opens the same Level 2
 * search panel as the drawer's SearchFieldButton, through the same
 * useSearchPanel hook (PRD D8), so the two triggers can never open subtly
 * different panels. Gated on the same `enableSiteWideSearch` preference as
 * the field, so the field it returns focus to on close is guaranteed to exist.
 *
 * Passes `searchRef` — not a ref to this button — as the drilldown's trigger
 * ref: this button sits outside the drawer's focus trap, so returning focus
 * to it on close would drop focus outside a focus-trapped panel. `searchRef`
 * points at the drawer's search field instead, threaded down from the
 * drawer root the same way the close-button ref already is, so both entry
 * points return focus to the field inside the menu.
 *
 * @param {HeaderSearchButtonProps} props
 * @param {ObjectElementRef} [props.searchRef] - Ref to the drawer's search field.
 */
const HeaderSearchButton = ({ searchRef }) => {
	const labels = useLabels();
	const { enableSiteWideSearch } = usePrefs();
	const openSearchPanel = useSearchPanel();
	const searchLabel = labels.get('NAV_DRAWER_SEARCH_LABEL');

	if (!isPrefValueEnabled(enableSiteWideSearch)) return null;

	return (
		<button
			type="button"
			className="btn btn-link text-decoration-none ws-nav-drawer__search-open drawer-header-icon d-none d-md-flex"
			onClick={() => openSearchPanel(searchRef)}
			data-testid="header-search-button"
		>
			<i
				className="ddc-icon ddc-icon-search ddc-icon-size-xlarge"
				aria-hidden="true"
			/>
			<span className="sr-only">{searchLabel}</span>
		</button>
	);
};

HeaderSearchButton.propTypes = {
	searchRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

HeaderSearchButton.defaultProps = {
	searchRef: null
};

export default HeaderSearchButton;
